import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { Section, SectionHeading } from '../components/ui/Section';
import { Prose, ProseParagraph, ProseList } from '../components/ui/Prose';
import { PullQuote, SectionDivider } from '../components/ui/PullQuote';
import { ContinueExploring } from '../components/ui/ContinueExploring';

const MetricTransformation = ({ metric, transformation, traditional, coherenceQualified, keyChange }) => (
  <div className="mb-12 last:mb-0">
    <div className="mb-4">
      <span className="font-serif text-lg">{metric}</span>
      <span className="mx-3 text-muted-foreground">→</span>
      <span className="font-serif text-lg font-medium">{transformation}</span>
    </div>
    
    <div className="grid md:grid-cols-2 gap-8 mt-6">
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-wider mb-3">Traditional Framing</p>
        <ProseList items={traditional} className="!mb-0" />
      </div>
      
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-wider mb-3">Coherence-Qualified Framing</p>
        <ProseList items={coherenceQualified} className="!mb-0" />
      </div>
    </div>
    
    <div className="mt-6 pt-4 border-t border-border/50">
      <p className="text-sm">
        <span className="font-medium">Key Change:</span>{' '}
        <span className="text-muted-foreground">{keyChange}</span>
      </p>
    </div>
  </div>
);

const CoherenceMetrics = () => {
  return (
    <div>
      <PageHeader 
        title="Coherence Metrics"
        subtitle="How success gets re-specified without slowing performance"
      />

      {/* The problem is not metrics */}
      <Section>
        <SectionHeading>The problem is not metrics</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Modern organizations are highly fluent in metrics: OKRs, KPIs, targets, dashboards, incentives. These instruments are not the issue. They are powerful tools for coordination and execution.
          </ProseParagraph>
          
          <ProseParagraph>
            The issue arises when metrics optimize <strong>for results in ways that quietly undermine the systems required to produce those results again.</strong>
          </ProseParagraph>
          
          <ProseParagraph>
            By governing the systems that define and optimize for success, Coherence OS ensures that the worlds those systems generate remain coherent as they scale.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What Coherence OS changes—precisely */}
      <Section>
        <SectionHeading>What Coherence OS changes—precisely</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS introduces a <strong>governing layer</strong> that sits <strong>above</strong> objectives and <strong>around</strong> optimization.
          </ProseParagraph>
          
          <div className="space-y-6 mt-8">
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Recognizes civilizational constraints</h4>
              <ProseParagraph className="!mb-0">
                Human dignity, planetary viability, and long-term coherence are treated as non-negotiable boundary conditions.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Qualifies success before it is pursued</h4>
              <ProseParagraph className="!mb-0">
                Objectives are specified such that achieving them cannot violate those conditions.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Maintains alignment as velocity increases</h4>
              <ProseParagraph className="!mb-0">
                Speed and scale are permitted only when trajectory remains coherent.
              </ProseParagraph>
            </div>
          </div>
          
          <ProseParagraph className="mt-8">
            The result is not slower performance. It is <strong>performance that does not undermine its own future</strong>.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* From raw metrics to coherence-qualified metrics */}
      <Section>
        <SectionHeading>From raw metrics to coherence-qualified metrics</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS does not replace OKRs or KPIs. It <strong>re-specifies what "good" looks like</strong>.
          </ProseParagraph>
          
          <ProseParagraph>
            Below are illustrative examples of how common metrics are transformed—not philosophically, but structurally.
          </ProseParagraph>
        </Prose>
        
        <div className="mt-12 space-y-16">
          <MetricTransformation
            metric="Velocity"
            transformation="Coherence-Qualified Velocity"
            traditional={[
              "How fast can we ship?",
              "How quickly can we scale?"
            ]}
            coherenceQualified={[
              "How fast can we move without degrading quality, trust, or human dignity?",
              "Does acceleration increase or erode long-term coherence?"
            ]}
            keyChange="Velocity remains a goal—but speed that creates downstream fragmentation is treated as failure, not success."
          />
          
          <MetricTransformation
            metric="Engagement"
            transformation="Beneficial Engagement"
            traditional={[
              "Time spent",
              "Frequency",
              "Retention at all costs"
            ]}
            coherenceQualified={[
              "Engagement that strengthens agency, dignity, and well-being",
              "Engagement that does not rely on addiction, manipulation, or erosion of trust"
            ]}
            keyChange="Engagement becomes a measure of value delivered, not attention extracted."
          />
          
          <MetricTransformation
            metric="Efficiency"
            transformation="Regenerative Efficiency"
            traditional={[
              "Cost reduction",
              "Resource minimization",
              "Output per unit input"
            ]}
            coherenceQualified={[
              "Efficiency that does not externalize depletion onto people or planet",
              "Processes that sustain capability rather than burn it out"
            ]}
            keyChange="Efficiency is preserved—but only when it does not undermine the system's ability to function tomorrow."
          />
          
          <MetricTransformation
            metric="Growth"
            transformation="Viability-Constrained Growth"
            traditional={[
              "Market expansion",
              "Revenue acceleration",
              "Scale at all costs"
            ]}
            coherenceQualified={[
              "Growth that remains compatible with ecological, social, and institutional thresholds",
              "Scale that does not collapse trust, culture, or environmental capacity"
            ]}
            keyChange="Growth becomes a function of viability, not domination."
          />
          
          <MetricTransformation
            metric="Retention"
            transformation="Whole-Human Retention"
            traditional={[
              "Lock-in",
              "Switching costs",
              "Attrition minimization"
            ]}
            coherenceQualified={[
              "Retention that reflects dignity, agency, and genuine value",
              "Participation that remains voluntary and life-giving"
            ]}
            keyChange="Retention signals health—not entrapment."
          />
        </div>
      </Section>

      <SectionDivider />

      {/* What this enables at scale */}
      <Section>
        <SectionHeading>What this enables at scale</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            By governing metrics rather than eliminating them, Coherence OS enables <strong>companies, organizations, and systems</strong> to:
          </ProseParagraph>
          
          <ProseList items={[
            "pursue ambitious OKRs without hidden harm",
            "align incentives across teams and time horizons",
            "maintain legitimacy as scale increases",
            "integrate AI acceleration without governance lag",
            "demonstrate credible alignment with SDG-oriented outcomes without performative reporting"
          ]} />
          
          <ProseParagraph>
            This is not compliance. It is <strong>design</strong>.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Why this matters at scale */}
      <Section>
        <SectionHeading>Why this matters at scale</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            At small scale, mis-specified metrics cause inefficiency. At global scale, they cause:
          </ProseParagraph>
          
          <ProseList items={[
            "dignity erosion",
            "trust collapse",
            "environmental depletion",
            "institutional fragility"
          ]} />
          
          <ProseParagraph>
            Coherence OS ensures that as systems grow more powerful, <strong>their definition of success grows more precise—not more reckless</strong>.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* A quiet but consequential shift */}
      <Section>
        <SectionHeading>A quiet but consequential shift</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            The most important change Coherence OS introduces is subtle <strong>but profound</strong>:
          </ProseParagraph>
          
          <ProseParagraph>
            Success is no longer defined solely by what is achieved—but by whether achievement strengthens the future rather than consuming it.
          </ProseParagraph>
          
          <ProseList items={[
            "This shift does not reduce ambition or performance.",
            "It makes it possible for success to compound without eroding the conditions that make future success possible."
          ]} />
          
          <ProseParagraph>
            <strong>This</strong> is how progress remains compatible with a future worth inheriting.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Where this fits */}
      <Section>
        <SectionHeading>Where this fits</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence Metrics sit:
          </ProseParagraph>
          
          <ProseList items={[
            "above OKRs",
            "alongside KPIs",
            "beneath strategy and within governance"
          ]} />
          
          <ProseParagraph>
            They do not replace existing performance systems. They <strong>make them safe to scale</strong>.
          </ProseParagraph>
        </Prose>
        
        {/* Continue exploring */}
        <ContinueExploring 
          links={[
            { label: 'What Is Coherence OS', href: '/what-is-coherence-os' },
            { label: 'Explore the category', href: '/the-category' },
            { label: 'For Investors', href: '/for-investors' },
          ]}
        />
      </Section>
    </div>
  );
};

export default CoherenceMetrics;
